import { useConstCallback } from '@workday/wcp-cargo-react';
import * as React from 'react';
import { useContext } from 'react';
import { GlobalPermission } from 'shared/models/user';
import AuthenticationContext from 'modules/core/auth/AuthenticationContext';
import { OrgContext, OrgLevelPermission } from 'modules/core/components/OrgProvider';
import { ConfidenceLevel, checkConfidence } from '../util/Confidence';

export interface FilterableObject {
  globalPermission?: GlobalPermission | GlobalPermission[];
  // checks that user has this permission or has EVERY one of the permissions passed in if multiple
  orgPermission?: OrgLevelPermission | OrgLevelPermission[];
  confidence?: ConfidenceLevel;
  hidden?: boolean;
  // This should ONLY be used if the object is replacing a deprecated object — in general, permissions should always be additive
  hideWithOrgPermission?: OrgLevelPermission | OrgLevelPermission[];
  // Checks if user has one of these permissions. Should NOT be used if orgPermission is passed
  hasSomeOrgPermission?: OrgLevelPermission[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
export type FilterableComponent<T extends FilterableObject> = React.ReactElement<T, any>;

export function useFilteredComponents<T extends FilterableObject>(comps: FilterableComponent<T>[]) {
  const filterFunction = useGetFilterFunction();
  return comps.filter((comp) => filterFunction(comp.props));
}

export function useFilteredObjects<T extends FilterableObject>(objs: T[]) {
  const filterFunction = useGetFilterFunction();
  return objs.filter(filterFunction);
}

export function useFilteredRecordValues<T extends FilterableObject>(obj: Record<string, T>) {
  const filterFunction = useGetFilterFunction();
  const filteredMap: Record<string, T> = {};
  for (const key of Object.keys(obj)) {
    if (filterFunction(obj[key])) {
      filteredMap[key] = obj[key];
    }
  }
  return filteredMap;
}

export function useGetFilterFunction<T extends FilterableObject>() {
  const authContext = useContext(AuthenticationContext);
  const orgContext = useContext(OrgContext);

  return useConstCallback((obj?: T) => {
    if (!obj) return false;
    const meetsConfidence = !obj.confidence || checkConfidence(obj.confidence);
    const hasGlobalPermission = !obj.globalPermission || authContext.hasGlobalPermission(obj.globalPermission);
    const isAdminToolsContext = !orgContext.currentOrg;
    const hasOrgPermission = isAdminToolsContext || !obj.orgPermission || orgContext.hasPermission(obj.orgPermission);
    // If orgPermission is passed, should not consider hasSomeOrgPermission. orgPermission is more restrictive
    const hasSomeOrgPermission =
      !!obj.orgPermission || !obj.hasSomeOrgPermission || orgContext.hasSomePermission(obj.hasSomeOrgPermission);
    // hide should only be true if this permission is passed in and the user has it
    const hasHideOrgPermission = obj.hideWithOrgPermission && orgContext.hasPermission(obj.hideWithOrgPermission);

    const isVisible = !obj.hidden;
    return (
      meetsConfidence &&
      hasGlobalPermission &&
      hasOrgPermission &&
      hasSomeOrgPermission &&
      !hasHideOrgPermission &&
      isVisible
    );
  });
}

// convenience wrapper around the above filter function that returns true if the object is undefined
// this is useful where the object being filtered is a visibility condition (and if nothing is set, there IS no condition)
export function useGetFilterFunctionEmptyTrue<T extends FilterableObject>() {
  const filterFunc = useGetFilterFunction();
  return useConstCallback((obj?: T) => !obj || filterFunc(obj));
}
